// @ts-strict-ignore
import {AfterViewInit, Component, effect, inject, input, output} from "@angular/core";
import {Checklist, ChecklistItem, ChecklistItemType, UserRecordingChecklist} from "@models";
import {GeneratedTypePredicates, SimChecklistService, TrackByMethods} from "@services";
import {ChecklistContextService} from "../services/checklist-context.service";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {ChecklistAnswerService} from "../services/checklist-answer.service";
import {AssessablePanelMode, AssessablePanelType} from "@common/components/assessment/utils";
import {configureChecklistProviders} from "../services/assessable.configuration";
import {AssessableUiService} from "../services/assessable-ui.service";
import {Translations} from "@translations";
import {AssessableProxyService} from "../services/assessable-proxy.service";

@UntilDestroy()
@Component({
	selector: "sim-checklist-panel",
	templateUrl: "./checklist-panel.component.html",
	styleUrls: ["./checklist-panel.component.scss"],
	providers: [...configureChecklistProviders()],
	standalone: false,
})
export class ChecklistPanelComponent implements AfterViewInit {
	// Enums
	protected readonly T = Translations;
	protected readonly AssessablePanelType = AssessablePanelType;
	protected readonly AssessablePanelMode = AssessablePanelMode;
	protected readonly ChecklistItemType = ChecklistItemType;
	protected readonly TrackByMethods = TrackByMethods;

	// I/O
	public assessable = input.required<Checklist | UserRecordingChecklist>();
	public mode = input.required<AssessablePanelMode>();
	public disabled = input(false);
	public isInArchive = input(false);
	public compactLayout = input(false);

	public onJumpToItem = output<number>();

	// Effects
	readonly #initContextEffect = effect(() => this._initContext(this.assessable()));
	readonly #changeModeEffect = effect((onCleanup) => {
		this.store.setMode(this.mode());
		if (this.mode() === AssessablePanelMode.Edit) {
			const sub = this._contextService.$savingPipeline.pipe(untilDestroyed(this)).subscribe();
			onCleanup(() => {
				sub.unsubscribe();
			});
		} else {
			const sub = this._answerService.$savingPipeline.pipe(untilDestroyed(this)).subscribe();
			onCleanup(() => sub.unsubscribe());
		}
	});
	readonly #disableEffect = effect(() => this.store.setDisabled(this.disabled()));

	// Injections
	public proxyService = inject(AssessableProxyService);
	protected store: AssessableUiService<ChecklistItem> = inject(AssessableUiService);
	private _contextService = inject(ChecklistContextService);
	private _answerService = inject(ChecklistAnswerService);
	private _checklistService = inject(SimChecklistService);

	public isStatusValid = this._contextService.isFormValid;
	public groupExpanded = this.store.groupExpanded;
	public itemExpanded = this.store.itemExpanded;
	public readonly $changedChecklist = this._contextService.$assessable;

	private _initContext(assessable: Checklist | UserRecordingChecklist) {
		this._contextService.initialize(assessable);
		this._answerService.initialize(assessable, (data) => {
			if (!GeneratedTypePredicates.isUserRecordingChecklist(assessable)) {
				return;
			}
			this._checklistService
				.updateAnswer({
					answer: data.answer,
					checklistId: data.checklistId,
					checklistItemId: data.checklistItemId,
					scenarioRecordingId: assessable.scenarioRecordingId,
				})
				.then();
		});
	}

	public getChecklist(): Checklist {
		return this._contextService.getAssessable();
	}

	public ngAfterViewInit() {
		this.store.expandFirstGroupAndItem();
	}

	public scrollAndEditGroupAndItem(groupId: string, itemId: string) {
		this.store.scrollAndEditItem(groupId, itemId);
	}
}
